import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/common/States";
import { useSession } from "@/context/SessionContext";
import { errMsg, MONTHS } from "@/lib/format";

const EMPTY = { name: "", code: "", legal_name: "", gstin: "", pan: "", state_code: "", address: "", city: "", state: "", pincode: "", phone: "", email: "", fy_start_month: 4 };
const TEXT_FIELDS = [
  ["legal_name", "Legal name"], ["gstin", "GSTIN"], ["pan", "PAN"], ["state_code", "GST state code"],
  ["address", "Address"], ["city", "City"], ["state", "State"], ["pincode", "PIN code"], ["phone", "Phone"], ["email", "Email"],
];

export const CompanyFormDialog = ({ open, onOpenChange, company, onSaved }) => {
  const { api, isPlatform } = useSession();
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setF(company ? { ...EMPTY, ...Object.fromEntries(Object.entries(company).map(([k, v]) => [k, v ?? ""])) } : EMPTY);
  }, [open, company]);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = Object.fromEntries(Object.keys(EMPTY).map((k) => [k, f[k] === "" ? null : f[k]]));
    body.fy_start_month = Number(f.fy_start_month);
    if (company && !isPlatform) delete body.code;
    if (company && isPlatform) body.status = f.status;
    try {
      const { data } = company ? await api.patch(`/companies/${company.id}`, body) : await api.post("/companies", body);
      toast.success(company ? "Company updated" : "Company created");
      onSaved?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="company-form-dialog">
        <DialogHeader>
          <DialogTitle>{company ? "Edit company" : "Onboard company"}</DialogTitle>
          <DialogDescription>Company profile, GST details and financial year settings.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
          <Field label="Company name *"><Input required value={f.name} onChange={set("name")} data-testid="company-name-input" /></Field>
          <Field label="Code *" hint="2–10 letters/digits, unique"><Input required disabled={!!company && !isPlatform} value={f.code} onChange={set("code")} data-testid="company-code-input" /></Field>
          {TEXT_FIELDS.map(([k, label]) => (
            <Field key={k} label={label}><Input value={f[k]} onChange={set(k)} data-testid={`company-${k.replace("_", "-")}-input`} /></Field>
          ))}
          <Field label="Financial year starts">
            <Select value={String(f.fy_start_month)} onValueChange={set("fy_start_month")}>
              <SelectTrigger data-testid="company-fy-select"><SelectValue /></SelectTrigger>
              <SelectContent>{MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          {company && isPlatform && (
            <Field label="Status">
              <Select value={f.status} onValueChange={set("status")}>
                <SelectTrigger data-testid="company-status-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="suspended">Suspended</SelectItem></SelectContent>
              </Select>
            </Field>
          )}
          <DialogFooter className="sm:col-span-2 mt-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="company-save-button">{saving ? "Saving…" : "Save company"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
